// src/components/home/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPasswordPage = () => {
  const API_PREFIX = '/api';
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');  // Add this line
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [status, setStatus] = useState('initial');
  const [error, setError] = useState('');

  // Add this useEffect right after your state declarations
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
      return;
    }
  }, [token, navigate]);

  // Add this check after useEffect
  if (!token) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setStatus('error');
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid or expired reset link. Please request a new password reset.');
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(API_PREFIX + '/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: formData.password
        })
      });

    // Add this more detailed error handling
    if (!response.ok) {
      const data = await response.json();
      // Use the error message from the backend
      throw new Error(data.detail || 'Invalid or expired reset link. Please request a new password reset.');
    }
  

      setStatus('success');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      <div className="max-w-md w-full p-8 rounded-lg">
        <h1 
          className="text-3xl font-bold mb-8 text-center"
          style={{ color: theme.mainPanelText }}
        >
          Reset Your Password
        </h1>
        
        {status === 'success' ? (
          <div className="text-center">
            <p 
              className="mb-4"
              style={{ color: theme.mainPanelText }}
            >
              Password reset successful! Redirecting to login...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium mb-2"
                style={{ color: theme.mainPanelText }}
              >
                New Password
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: theme.inputBg,
                  borderColor: theme.inputBorder,
                  color: theme.inputText
                }}
                required
              />
            </div>

            <div>
              <label 
                htmlFor="confirmPassword" 
                className="block text-sm font-medium mb-2"
                style={{ color: theme.mainPanelText }}
              >
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: theme.inputBg,
                  borderColor: theme.inputBorder,
                  color: theme.inputText
                }}
                required
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 transition duration-150"
              style={{
                backgroundColor: theme.buttonBg,
                color: theme.buttonText,
                opacity: status === 'loading' ? 0.7 : 1
              }}
            >
              {status === 'loading' ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;